import React, { useRef, useState } from 'react';
import axios from 'axios';
import { UserCircle, Camera, X as XIcon } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Circular avatar with tap-to-upload behaviour. Shows current avatar or an
 * initial fallback. Uploads to /api/profiles/avatar and calls onUploaded with
 * the fresh URL so the parent can update its user state.
 */
export default function AvatarUpload({ user, size = 96, onUploaded, testId = 'avatar-upload' }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const avatarUrl = user?.avatar_url
    ? (user.avatar_url.startsWith('http')
        ? user.avatar_url
        : process.env.REACT_APP_BACKEND_URL + user.avatar_url)
    : null;

  const initial = (user?.display_name || 'B').trim().charAt(0).toUpperCase();

  const pick = () => inputRef.current?.click();
  const clear = async (e) => {
    e.stopPropagation();
    setBusy(true);
    setErr('');
    try {
      await axios.put(`${API_URL}/auth/me`, { avatar_url: '' }, { withCredentials: true });
      onUploaded?.(null);
    } catch (e) {
      setErr('Could not clear');
    } finally {
      setBusy(false);
    }
  };

  const handleFile = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setErr('Image must be under 5 MB');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API_URL}/profiles/avatar`, form, {
        withCredentials: true,
      });
      onUploaded?.(data.avatar_url);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const style = { width: size, height: size };

  return (
    <div className="inline-flex flex-col items-center">
      <button
        type="button"
        onClick={pick}
        disabled={busy}
        className="relative rounded-full overflow-hidden bg-graphite/8 border-2 border-hairline flex items-center justify-center shadow-ios active:scale-[0.97] transition"
        style={style}
        data-testid={testId}
        aria-label="Change profile picture"
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt="Profile"
            className="w-full h-full object-cover"
            data-testid={`${testId}-image`}
          />
        ) : (
          <span className="text-4xl font-black font-heading text-graphite/70">{initial}</span>
        )}
        <span className="absolute bottom-1 right-1 w-7 h-7 rounded-full bg-graphite text-bone flex items-center justify-center shadow-ios border-2 border-bone">
          <Camera size={12} weight="fill" />
        </span>
        {busy && (
          <span className="absolute inset-0 bg-graphite/30 flex items-center justify-center text-bone text-[10px] uppercase tracking-widest font-bold">
            …
          </span>
        )}
      </button>
      {avatarUrl && (
        <button
          type="button"
          onClick={clear}
          className="mt-2 text-[10px] uppercase tracking-[0.18em] font-bold text-graphite/50 hover:text-userRed inline-flex items-center gap-1"
          data-testid={`${testId}-clear`}
        >
          <XIcon size={10} weight="bold" /> Remove
        </button>
      )}
      {err && <p className="mt-1 text-[10px] text-userRed font-bold">{err}</p>}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={handleFile}
        className="hidden"
        data-testid={`${testId}-input`}
      />
    </div>
  );
}
